import { useLocation } from '@reach/router'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { getNextMidnight, getRemainingTimeUntilMsTimestamp } from './CountdownTimerUtil'

interface CountdownTimerProps {
  setDisplayDeal: (value: boolean) => void
  targetTime?: Date | null
}

export default function CountdownTimer({ setDisplayDeal, targetTime }: CountdownTimerProps) {
  const { pathname } = useLocation()

  useEffect(() => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'banner_timer_loaded', { page: pathname.replace(/^\//, '') })
    }
  }, [])

  const [remainingTime, setRemainingTime] = useState<{
    hours: string | null
    minutes: string | null
    seconds: string | null
  }>({
    hours: '00',
    minutes: '00',
    seconds: '00'
  })

  const normalizeTime = (time: {
    seconds: string | null
    minutes: string | null
    hours?: string | null
  }) => {
    return {
      seconds: time.seconds,
      minutes: time.minutes,
      hours: time.hours ?? null // default hours to null if undefined
    }
  }

  useEffect(() => {
    const updateRemainingTime = () => {
      const countdownTarget = targetTime ? dayjs(targetTime) : getNextMidnight()
      const newTime = getRemainingTimeUntilMsTimestamp(true, countdownTarget)

      // When time runs out and counting to midnight, reset to next midnight
      if (!targetTime && (newTime.minutes === null || newTime.seconds === null)) {
        const nextMidnight = getNextMidnight()
        setRemainingTime(normalizeTime(getRemainingTimeUntilMsTimestamp(true, nextMidnight)))
      } else {
        setRemainingTime(normalizeTime(newTime))
      }
    }

    updateRemainingTime()
    const intervalId = setInterval(updateRemainingTime, 1000)
    return () => clearInterval(intervalId)
  }, [targetTime])

  useEffect(() => {
    const valid = remainingTime.hours !== null && remainingTime.minutes !== null && remainingTime.seconds !== null
    setDisplayDeal(valid)
  }, [remainingTime])

  return (
    <>
      {(remainingTime.seconds !== null || remainingTime.minutes !== null || remainingTime.hours !== null) && (
        <div className="flex flex-row justify-center items-center">
          <div className="m-2 text-center flex-col">
            <div className="w-12 h-6 md:w-16 md:h-8 bg-white rounded-full text-center content-center">
              <p className="text-center text-base md:text-xl text-primary font-sans font-bold">{remainingTime.hours}</p>
            </div>
            <p className="text-center text-sm text-white font-sans font-medium">hrs</p>
          </div>
          {/* <span className="font-bold">:</span> */}
          <div className="m-2 text-center flex-col">
            <div className="w-12 h-6 md:w-16 md:h-8 bg-white rounded-full text-center content-center">
              <p className="text-center text-base md:text-xl text-primary font-sans font-bold">
                {remainingTime.minutes}
              </p>
            </div>
            <p className="text-center text-sm text-white font-sans font-medium">min</p>
          </div>
          {/* <span className="font-bold">:</span> */}
          <div className="m-2 text-center flex-col">
            <div className="w-12 h-6 md:w-16 md:h-8 bg-white rounded-full text-center content-center">
              <p className="text-center text-base md:text-xl text-primary font-sans font-bold">
                {remainingTime.seconds}
              </p>
            </div>
            <p className="text-center text-sm text-white font-sans font-medium">sec</p>
          </div>
        </div>
      )}
    </>
  )
}
